import { useState, useMemo } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, TrendingUp } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { FilterResultStockSchema } from "@/pages/manual-testing";

interface SortableGPTableProps {
  passedStocks: FilterResultStockSchema[];
  failedStocks: FilterResultStockSchema[];
}

type SortConfig = {
  key: string | null;
  direction: 'asc' | 'desc' | null;
};

export default function SortableGPTable({ passedStocks, failedStocks }: SortableGPTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });

  const allStocks = useMemo(() => {
    return [...passedStocks, ...failedStocks];
  }, [passedStocks, failedStocks]);

  // ✅ Guard: skip stocks with no variables (failed crypto stocks)
  const gpVariables = useMemo(() => {
    const gpVars = new Set<string>();
    allStocks.forEach(stock => {
      if (!stock.variables) return;
      Object.keys(stock.variables).forEach(key => {
        if (key.includes('_Gp')) {
          gpVars.add(key);
        }
      });
    });
    return Array.from(gpVars).sort();
  }, [allStocks]);

  // ✅ Guard: optional chain on variables in sort comparison
  const sortedStocks = useMemo(() => {
    if (!sortConfig.key) return allStocks;

    return [...allStocks].sort((a, b) => {
      const aValue = a.variables?.[sortConfig.key!] ?? -Infinity;
      const bValue = b.variables?.[sortConfig.key!] ?? -Infinity;

      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [allStocks, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current.key === key) {
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    if (sortConfig.direction === 'asc') {
      return <ArrowUp className="w-4 h-4 text-blue-600" />;
    }
    return <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  // ✅ Show message if no GP variables exist, but only render the section if we have test results
  if (allStocks.length === 0) return null;

  if (gpVariables.length === 0) {
    return (
      <Card className="shadow-xl border-0 bg-gradient-to-br from-blue-50 to-purple-50">
        <CardContent className="pt-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800">GP Variables Analysis</h3>
              <p className="text-gray-600">Click column headers to sort by GP values • Green = Passed, Red = Failed</p>
            </div>
          </div>
          <div className="text-center py-12 px-6 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-700 mb-2">No GP Variables Found</h4>
            <p className="text-gray-500 mb-4">
              No GP (Gap Percentage) variables are present in the selected filter functions.
              GP variables are derived from MACD calculations and appear when filters use MACD-based logic.
            </p>
            <div className="text-sm text-gray-400 bg-gray-50 p-3 rounded-lg inline-block">
              To see GP variables, ensure your filter functions reference MACD variables that generate Gp (Gap) variables.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl border-0 bg-gradient-to-br from-blue-50 to-purple-50">
      <CardContent className="pt-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-800">GP Variables Analysis</h3>
            <p className="text-gray-600">Click column headers to sort by GP values • Green = Passed, Red = Failed</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-lg">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="sticky left-0 z-10 bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-4 text-left">
                  <div className="font-bold text-gray-900">Symbol</div>
                </th>
                <th className="px-4 py-4 text-center min-w-[100px]">
                  <div className="font-bold text-gray-900 text-sm">Status</div>
                </th>
                {gpVariables.map(gpVar => (
                  <th key={gpVar} className="px-4 py-4 text-center min-w-[140px]">
                    <button
                      type="button"
                      onClick={() => handleSort(gpVar)}
                      className="flex items-center justify-center space-x-2 w-full hover:bg-blue-50 rounded-lg p-2 transition-colors"
                    >
                      <span className="font-bold text-gray-900 text-sm">
                        {gpVar}
                      </span>
                      {getSortIcon(gpVar)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedStocks.map((stock, idx) => (
                <tr
                  key={`${stock.symbol}-${stock.filter_id ?? idx}`}  // ✅ fixed key
                  className={`border-t border-gray-200 hover:bg-blue-50 transition-colors ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <td className="sticky left-0 z-10 px-4 py-3 font-bold text-gray-900 bg-inherit">
                    {stock.symbol}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        stock.passed
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {stock.passed ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                  {gpVariables.map(gpVar => {
                    const value = stock.variables?.[gpVar];  // ✅ optional chain
                    const isHighlighted = sortConfig.key === gpVar;
                    return (
                      <td
                        key={gpVar}
                        className={`px-4 py-3 text-center font-medium ${
                          isHighlighted
                            ? 'bg-blue-100 text-blue-900'
                            : 'text-gray-700'
                        }`}
                      >
                        {value !== undefined ? value.toFixed(2) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>
              Showing <span className="font-bold text-gray-900">{sortedStocks.length}</span> stocks with GP variables
            </span>
            <span className="text-green-700 font-medium">
              {passedStocks.length} passed
            </span>
            <span className="text-red-700 font-medium">
              {failedStocks.length} failed
            </span>
          </div>
          {sortConfig.key && (
            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 rounded-lg">
              <span className="font-medium text-blue-900">
                Sorted by: {sortConfig.key}
              </span>
              <button
                onClick={() => setSortConfig({ key: null, direction: null })}
                className="text-blue-700 hover:text-blue-900 underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}